// A tervező nem mentett piszkozata (pl. lejárt munkamenet / újratöltés ellen).
// IndexedDB-ben, mert a képek data URL-ként akár több MB-osak — a localStorage kevés.
// Kulcs: `${userId}:${productId}`, így közös gépen sem látszik más piszkozata.
// Minden hiba csendes: a piszkozat kényelmi funkció, a tervezést nem akaszthatja meg.
import { DesignData } from '../types/design';

export const DRAFT_VERSION = 1;
export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface DesignDraft {
  version: 1;
  savedAt: string; // ISO időpont
  designData: DesignData;
  variant: { gender: string; color: string; size: string };
}

/** Kulcs-érték tároló (IndexedDB, tesztben memória). Hibánál elutasít. */
export interface DraftStorageAdapter {
  get(key: string): Promise<unknown>;
  put(key: string, value: DesignDraft): Promise<void>;
  delete(key: string): Promise<void>;
  clear(): Promise<void>;
  /** Végigmegy minden bejegyzésen, és törli, amelyikre a predikátum igaz. */
  sweep(shouldDelete: (key: string, value: unknown) => boolean): Promise<void>;
}

export interface DraftStore {
  /** Mentés. Zárolt tárolóba (kijelentkezés után) vagy elavult `epoch`-kal nem ír. */
  save(key: string, draft: DesignDraft, epoch?: number): Promise<void>;
  load(key: string): Promise<DesignDraft | null>;
  clear(key: string): Promise<void>;
  /** Minden piszkozat törlése + zárolás (kijelentkezés); az `unlock` (bejelentkezés) oldja fel. */
  clearAll(): Promise<void>;
  /** Minden más kulcs törlése (bejelentkezéskor: más felhasználók piszkozatai közös gépen). */
  clearAllExcept(prefix: string): Promise<void>;
  /** Lejárt / érvénytelen piszkozatok törlése — oldalmunkamenetenként egyszer fut le. */
  pruneExpiredOnce(): Promise<void>;
  unlock(): void;
  /** Minden clearAll növeli; a régebbi epoch-kal indított mentés elvész. */
  epoch(): number;
}

export const draftKey = (userId: number | string, productId: number | string) => `${userId}:${productId}`;

function isDraft(value: unknown): value is DesignDraft {
  if (!value || typeof value !== 'object') return false;
  const v = value as Partial<DesignDraft>;
  return (
    v.version === DRAFT_VERSION &&
    typeof v.savedAt === 'string' &&
    !!v.designData &&
    Array.isArray(v.designData.textElements) &&
    Array.isArray(v.designData.imageElements) &&
    typeof v.designData.backgroundColor === 'string' &&
    !!v.variant &&
    typeof v.variant.gender === 'string' &&
    typeof v.variant.color === 'string' &&
    typeof v.variant.size === 'string'
  );
}

/** Más verzió, sérült, vagy 7 napnál régebbi. */
function isStale(value: unknown, nowMs: number): boolean {
  if (!isDraft(value)) return true;
  const savedAt = Date.parse(value.savedAt);
  return !Number.isFinite(savedAt) || nowMs - savedAt > DRAFT_MAX_AGE_MS;
}

export function createDraftStore(adapter: DraftStorageAdapter, now: () => number = Date.now): DraftStore {
  let warned = false;
  let locked = false;
  let epoch = 0;
  let pruned = false;
  const warn = (e: unknown) => {
    if (!warned) {
      warned = true;
      console.warn('A tervpiszkozat tárolása nem érhető el.', e);
    }
  };
  const quietly = async (fn: () => Promise<void>) => {
    try {
      await fn();
    } catch (e) {
      warn(e);
    }
  };

  return {
    save: (key, draft, atEpoch) => {
      if (locked || (atEpoch !== undefined && atEpoch !== epoch)) return Promise.resolve();
      return quietly(() => adapter.put(key, draft));
    },
    clear: (key) => quietly(() => adapter.delete(key)),
    clearAll: () => {
      locked = true;
      epoch += 1;
      return quietly(() => adapter.clear());
    },
    clearAllExcept: (prefix) => quietly(() => adapter.sweep((key) => !key.startsWith(prefix))),
    pruneExpiredOnce: () => {
      if (pruned) return Promise.resolve();
      pruned = true;
      const nowMs = now();
      return quietly(() => adapter.sweep((_key, value) => isStale(value, nowMs)));
    },
    unlock: () => {
      locked = false;
    },
    epoch: () => epoch,
    async load(key) {
      try {
        const value = await adapter.get(key);
        if (value === undefined || value === null) return null;
        if (isStale(value, now())) {
          await quietly(() => adapter.delete(key));
          return null;
        }
        return value as DesignDraft;
      } catch (e) {
        warn(e);
        return null;
      }
    },
  };
}

/** A törlés legfeljebb `ms` ideig tartsa fel a hívót (pl. beragadt IndexedDB kijelentkezéskor). */
export function settleWithin(promise: Promise<unknown> | undefined, ms: number): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(resolve, ms);
  });
  return Promise.race([Promise.resolve(promise).then(() => undefined, () => undefined), timeout]).finally(() =>
    clearTimeout(timer),
  );
}

const DB_NAME = 'gt-design-drafts';
const STORE_NAME = 'drafts';

function requestToPromise<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

const defaultFactory = (): IDBFactory | undefined =>
  typeof indexedDB === 'undefined' || !indexedDB ? undefined : indexedDB;

/** IndexedDB-adapter; ha a böngészőben nincs IndexedDB, minden hívás elutasít. */
export function createIndexedDbAdapter(getFactory: () => IDBFactory | undefined = defaultFactory): DraftStorageAdapter {
  let dbPromise: Promise<IDBDatabase> | null = null;

  const open = (): Promise<IDBDatabase> => {
    const factory = getFactory();
    if (!factory) return Promise.reject(new Error('Nincs IndexedDB.'));
    if (dbPromise) return dbPromise;
    const p = new Promise<IDBDatabase>((resolve, reject) => {
      let settled = false;
      const req = factory.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        if (!req.result.objectStoreNames.contains(STORE_NAME)) req.result.createObjectStore(STORE_NAME);
      };
      req.onsuccess = () => {
        const db = req.result;
        if (settled) {
          // Már elutasítottuk (blocked) — a későn sikerült kapcsolat ne maradjon nyitva.
          db.close();
          return;
        }
        settled = true;
        const forget = () => {
          if (dbPromise === p) dbPromise = null;
        };
        // Másik lap újabb sémaverziót nyit: engedjük el, a következő hívás újranyit.
        db.onversionchange = () => {
          db.close();
          forget();
        };
        db.onclose = forget;
        resolve(db);
      };
      req.onerror = () => {
        settled = true;
        reject(req.error);
      };
      req.onblocked = () => {
        if (settled) return;
        settled = true;
        reject(new Error('Az IndexedDB blokkolt.'));
      };
    });
    dbPromise = p;
    // Sikertelen megnyitás után a következő hívás újrapróbálhatja.
    p.catch(() => {
      if (dbPromise === p) dbPromise = null;
    });
    return p;
  };

  const transaction = async (mode: IDBTransactionMode) => {
    const db = await open();
    let tx: IDBTransaction;
    try {
      tx = db.transaction(STORE_NAME, mode);
    } catch (e) {
      // Pl. InvalidStateError lezárt kapcsolaton: a következő hívás újranyit.
      dbPromise = null;
      throw e;
    }
    // Az írás csak a tranzakció lezárultával végleges (pl. kvótahiba itt derül ki).
    const done = new Promise<void>((resolve, reject) => {
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error ?? new Error('A tranzakció megszakadt.'));
    });
    done.catch(() => undefined); // olvasásnál nem várjuk meg — ne legyen kezeletlen elutasítás
    return { store: tx.objectStore(STORE_NAME), done };
  };

  const write = async (fn: (store: IDBObjectStore) => IDBRequest) => {
    const { store, done } = await transaction('readwrite');
    await requestToPromise(fn(store));
    await done;
  };

  return {
    get: async (key) => {
      const { store } = await transaction('readonly');
      return requestToPromise(store.get(key));
    },
    put: (key, value) => write((s) => s.put(value, key)),
    delete: (key) => write((s) => s.delete(key)),
    clear: () => write((s) => s.clear()),
    sweep: async (shouldDelete) => {
      const { store, done } = await transaction('readwrite');
      await new Promise<void>((resolve, reject) => {
        const req = store.openCursor();
        req.onsuccess = () => {
          const cursor = req.result;
          if (!cursor) {
            resolve();
            return;
          }
          if (shouldDelete(String(cursor.key), cursor.value)) cursor.delete();
          cursor.continue();
        };
        req.onerror = () => reject(req.error);
      });
      await done;
    },
  };
}

const designDraftStore = createDraftStore(createIndexedDbAdapter());
export default designDraftStore;

// --- A tervező oldal döntései (tisztán, tesztelhetően) ---

export type DraftVariant = DesignDraft['variant'];

/** A kulcshoz tartozó legutóbbi állapot — kulcsváltáskor / oldalelhagyáskor ezt mentjük el. */
export interface DraftSnapshot {
  key: string;
  userId: number | string;
  designData: DesignData;
  variant: DraftVariant;
  /** A tároló epoch-ja a pillanatkép készültekor (kijelentkezés után elavul). */
  epoch?: number;
}

export const isDesignEmpty = (d: DesignData) => d.textElements.length === 0 && d.imageElements.length === 0;

/** Kulcsváltáskor (termékváltás, betöltés): a régi kulcs állapotát el kell-e menteni.
 *  Nem, ha nincs mit, ha a kulcs nem változott, vagy ha közben más (vagy senki) lett
 *  bejelentkezve — kijelentkezés után a piszkozat nem kerülhet vissza. */
export function snapshotToFlush(
  snapshot: DraftSnapshot | null,
  currentUserId: number | string | undefined,
  newKey: string | null,
): DraftSnapshot | null {
  if (!snapshot || snapshot.key === newKey) return null;
  if (currentUserId === undefined || snapshot.userId !== currentUserId) return null;
  return snapshot;
}

/** Üres terv: a piszkozat törlése; különben mentés (a pillanatkép epoch-jával). */
export function persistSnapshot(
  store: DraftStore,
  snapshot: DraftSnapshot,
  savedAt: string = new Date().toISOString(),
): Promise<void> {
  if (isDesignEmpty(snapshot.designData)) return store.clear(snapshot.key);
  return store.save(
    snapshot.key,
    { version: DRAFT_VERSION, savedAt, designData: snapshot.designData, variant: snapshot.variant },
    snapshot.epoch,
  );
}

interface VariantLike {
  gender?: { name?: string } | null;
  color: string;
  size: string;
}

/** A visszaállított Fazon → Szín → Méret választás lépcsőzetes ellenőrzése a termék
 *  aktuális aktív változatai alapján; ami már nem létezik, az (és utána minden) üres lesz. */
export function validateVariantSelection(variants: VariantLike[], sel: DraftVariant): DraftVariant {
  const genderOf = (v: VariantLike) => v.gender?.name ?? '';
  const genders = new Set(variants.map(genderOf).filter(Boolean));
  const needsGender = genders.size > 1;
  const gender = needsGender && genders.has(sel.gender) ? sel.gender : '';
  if (needsGender && !gender) return { gender: '', color: '', size: '' };
  const forGender = needsGender ? variants.filter((v) => genderOf(v) === gender) : variants;
  const color = forGender.some((v) => v.color === sel.color) ? sel.color : '';
  if (!color) return { gender, color: '', size: '' };
  const forColor = forGender.filter((v) => v.color === color);
  const size = forColor.some((v) => v.size === sel.size) ? sel.size : '';
  return { gender, color, size };
}

/** Régi tervező-verzió maradéka: a „Mentés" a teljes tervet (base64 fotókkal) a
 *  localStorage `design_<név>` kulcsaira írta — sosem olvasta vissza, kijelentkezéskor
 *  sem törlődött. Adatvédelmi okból egyszer, induláskor eltávolítjuk. */
export function purgeLegacyLocalDesigns(storage: Storage | undefined = typeof localStorage === 'undefined' ? undefined : localStorage): number {
  if (!storage) return 0;
  try {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i);
      if (key && key.startsWith('design_')) keys.push(key);
    }
    keys.forEach((key) => storage.removeItem(key));
    return keys.length;
  } catch {
    return 0; // pl. letiltott tároló — nincs teendő
  }
}
